import React, { useState } from "react";
import { Link } from "react-router-dom";
import useReadMyMeetings from "../Libs/useReadMyMeetings";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./Haggling.css";
import { FiAlertCircle } from "react-icons/fi";

// Success Modal Component (unchanged)
const SuccessModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="haggling-modal-overlay">
      <div className="haggling-modal">
        <div className="haggling-modal-header">
          <h3>Success</h3>
          <button className="haggling-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="haggling-modal-content">
          <div className="haggling-success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#F0FFEA" />
              <path d="M44 24L28 40L20 32" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="haggling-success-message">
            Congratulations! Both you and the seller have agreed on a final price.
          </p>
          <button className="haggling-modal-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default function Haggling() {
  const [expandedMeetingId, setExpandedMeetingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { meetings, isLoading: meetingsLoading, isError: meetingsError, error: meetingsErrorData } = useReadMyMeetings();

  // Debug logging
  console.log('Vessel Owner Haggling Component - Meetings Data:', meetings);
  console.log('Vessel Owner Haggling Component - Loading State:', meetingsLoading);
  console.log('Vessel Owner Haggling Component - Error State:', meetingsError);
  console.log('Vessel Owner Haggling Component - Error Data:', meetingsErrorData);

  // Helper function to format currency
  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    const numAmount = typeof amount === 'object' && amount.$numberDecimal 
      ? parseFloat(amount.$numberDecimal) 
      : parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(numAmount);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="haggling-container">
      <div className="haggling-schedule-card">
        <div className="haggling-header">
          <h2 className="haggling-title">My Haggling Sessions</h2>
          <div className="haggling-search">
            <input type="text" placeholder="Search Sessions" />
            <svg className="haggling-search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.5 17.5L12.5 12.5M14.1667 8.33333C14.1667 11.555 11.555 14.1667 8.33333 14.1667C5.11167 14.1667 2.5 11.555 2.5 8.33333C2.5 5.11167 5.11167 2.5 8.33333 2.5C11.555 2.5 14.1667 5.11167 14.1667 8.33333Z" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
        <div className="haggling-schedule-list">
          {meetingsLoading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div className="haggling-schedule-item" key={idx}>
                <div className="haggling-schedule-content">
                  <div className="haggling-left-column">
                    <Skeleton height={20} width={100} style={{ marginBottom: 8 }} />
                    <Skeleton height={20} width={60} />
                  </div>
                  <div className="haggling-middle-column">
                    <Skeleton height={20} width={120} style={{ marginBottom: 8 }} />
                    <Skeleton height={20} width={180} />
                  </div>
                  <div className="haggling-right-column">
                    <Skeleton height={36} width={120} style={{ marginBottom: 8 }} />
                    <Skeleton height={36} width={120} />
                  </div>
                </div>
              </div>
            ))
          ) : meetingsError ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FiAlertCircle size={48} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 18, fontWeight: 500 }}>Pending Approval</div>
              <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>Your haggling sessions are being reviewed.</div>
            </div>
          ) : meetings && meetings.length > 0 ? (
            meetings.map((meeting) => (
              <div
                key={meeting._id}
                className={`haggling-schedule-item ${expandedMeetingId === meeting._id ? 'expanded' : ''}`}
              >
                <div className="haggling-schedule-content">
                  <div className="haggling-left-column">
                    <div className="haggling-date-group">
                      <div className="haggling-label">Scheduled Date</div>
                      <div className="haggling-value">{formatDate(meeting.scheduled_time)}</div>
                    </div>
                    <div className="haggling-date-group">
                      <div className="haggling-label">Time</div>
                      <div className="haggling-value">{formatTime(meeting.scheduled_time)}</div>
                    </div>
                    <div className="haggling-date-group">
                      <div className="haggling-label">Duration</div>
                      <div className="haggling-value">{meeting.duration || 0} minutes</div>
                    </div>
                  </div>
                  <div className="haggling-middle-column">
                    <div className="haggling-label">Session Details</div>
                    <div className="haggling-vessel-name">
                      <strong>Base Amount:</strong> {formatCurrency(meeting.base_amount, meeting.currency)}
                    </div>
                    <div className="haggling-vessel-name">
                      <strong>Final Amount:</strong> {formatCurrency(meeting.final_amount, meeting.currency)}
                    </div>
                    <div className="haggling-vessel-name">
                      <strong>Status:</strong> {meeting.status || 'N/A'}
                    </div>
                    {expandedMeetingId === meeting._id && (
                      <div className="haggling-meeting-details">
                        <div className="haggling-detail-row">
                          <span className="haggling-detail-label">Meeting ID:</span>
                          <span className="haggling-detail-value">{meeting._id}</span>
                        </div>
                        <div className="haggling-detail-row">
                          <span className="haggling-detail-label">UID:</span>
                          <span className="haggling-detail-value">{meeting.uid}</span>
                        </div>
                        <div className="haggling-detail-row">
                          <span className="haggling-detail-label">Final Amount:</span>
                          <span className="haggling-detail-value">{formatCurrency(meeting.final_amount, meeting.currency)}</span>
                        </div>
                        <div className="haggling-detail-row">
                          <span className="haggling-detail-label">Negotiation State:</span>
                          <span className={`haggling-detail-value haggling-state-${meeting.negotiation_state?.toLowerCase()}`}>
                            {meeting.negotiation_state || 'N/A'}
                          </span>
                        </div>
                        <div className="haggling-detail-row">
                          <span className="haggling-detail-label">Created:</span>
                          <span className="haggling-detail-value">{formatDate(meeting.created_at)}</span>
                        </div>
                        <div className="haggling-detail-row">
                          <span className="haggling-detail-label">Updated:</span>
                          <span className="haggling-detail-value">{formatDate(meeting.updated_at)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="haggling-right-column">
                    <button
                      className="haggling-join-button"
                      onClick={() => setExpandedMeetingId(expandedMeetingId === meeting._id ? null : meeting._id)}
                    >
                      {expandedMeetingId === meeting._id ? 'Hide Details' : 'Show Details'}
                    </button>
                    {meeting.guest1_meeting_link ? (
                      <a
                        href={meeting.guest1_meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="haggling-join-button"
                        style={{ marginTop: 8, textDecoration: 'none', display: 'inline-block' }}
                      >
                        Join Session
                      </a>
                    ) : (
                      <Link
                        to={`/dashboard/haggling/bidding/${meeting._id}`}
                        className="haggling-join-button"
                        style={{ marginTop: 8 }}
                      >
                        Join Session
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FiAlertCircle size={48} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 18, fontWeight: 500 }}>No haggling sessions found.</div>
              <div style={{ fontSize: 14, color: '#aaa', marginTop: 4 }}>You have no scheduled haggling sessions at this time.</div>
            </div>
          )}
        </div>
      </div>
      <SuccessModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
} 